import { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  Grid,
  GridItem,
  Select,
} from '@chakra-ui/react';

import SelectSearch from 'components/fields/SelectSearch';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import type { PropertyClaim } from 'api/quote-marketplace';
import ValidatedField from './ValidatedField';
import ValidatedDateField from './ValidatedDateField';
import { useValidationSchema } from '../hooks';

interface ClaimCauseOption {
  value: string;
  label: string;
  available_for_types?: string[];
}

export interface PropertyClaimModalProps {
  isOpen: boolean;
  onClose: () => void;
  claim: PropertyClaim;
  onChange: (claim: PropertyClaim) => void;
  onSave: () => void;
  claimTypes: { value: string; label: string }[];
  claimCauses: ClaimCauseOption[];
}

const PropertyClaimModal = ({
  isOpen,
  onClose,
  claim,
  onChange,
  onSave,
  claimTypes,
  claimCauses,
}: PropertyClaimModalProps) => {
  const { isFieldRequired } = useValidationSchema();
  const claimEntity = claim as unknown as Record<string, unknown>;
  const isAmountRequired = isFieldRequired(
    'location/claim',
    'payout_amount',
    claimEntity,
  );
  const isAmountNonPositive =
    claim.payout_amount != null && claim.payout_amount <= 0;

  const filteredCauses = useMemo(() => {
    if (!claim.type_code) {
      return claimCauses;
    }
    return claimCauses.filter(
      (cause) =>
        !cause.available_for_types?.length ||
        cause.available_for_types.includes(claim.type_code!),
    );
  }, [claimCauses, claim.type_code]);

  const handleTypeChange = (newTypeCode: string | null) => {
    const updatedClaim = {
      ...claim,
      type_code: newTypeCode,
    };
    if (newTypeCode && claim.cause_code) {
      const causeStillValid = claimCauses.some(
        (c) =>
          c.value === claim.cause_code &&
          (!c.available_for_types?.length ||
            c.available_for_types.includes(newTypeCode)),
      );
      if (!causeStillValid) {
        updatedClaim.cause_code = null;
      }
    }
    onChange(updatedClaim);
  };

  const handleDateChange = (date: Date | null) => {
    onChange({
      ...claim,
      date: formatDateToString(date),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{claim.id ? 'Edit Claim' : 'Add Claim'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem>
              <ValidatedDateField
                itemPath="location/claim"
                fieldPath="date"
                label="Date"
                date={parseDateString(claim.date || '')}
                onChange={handleDateChange}
                entity={claimEntity}
                maxDate={new Date()}
              />
            </GridItem>
            <GridItem>
              <ValidatedField
                itemPath="location/claim"
                fieldPath="payout_amount"
                label="Amount"
                entity={claimEntity}
                isInvalid={isAmountNonPositive}
                errorMessage="Amount must be greater than 0"
              >
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={claim.payout_amount ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...claim,
                      payout_amount: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                />
              </ValidatedField>
            </GridItem>
            <GridItem>
              <ValidatedField
                itemPath="location/claim"
                fieldPath="type_code"
                label="Type"
                entity={claimEntity}
              >
                <Select
                  variant="main"
                  size="sm"
                  value={claim.type_code || ''}
                  onChange={(e) => handleTypeChange(e.target.value || null)}
                >
                  {claimTypes.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </ValidatedField>
            </GridItem>
            <GridItem colSpan={2}>
              <ValidatedField
                itemPath="location/claim"
                fieldPath="cause_code"
                label="Cause"
                entity={claimEntity}
                isDisabled={!claim.type_code}
              >
                <SelectSearch
                  options={filteredCauses}
                  value={claim.cause_code || ''}
                  onChange={(value) =>
                    onChange({
                      ...claim,
                      cause_code: value ? String(value) : null,
                    })
                  }
                  isDisabled={!claim.type_code}
                  placeholder={
                    !claim.type_code ? 'Select a type first' : 'Select...'
                  }
                />
              </ValidatedField>
            </GridItem>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="yellow"
            onClick={onSave}
            isDisabled={
              (isAmountRequired && claim.payout_amount == null) ||
              isAmountNonPositive
            }
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PropertyClaimModal;
